import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControl, Grid, ListItemText, TextField, Typography, useTheme } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import QuickDateFilters, { DateRange } from '../components/QuickDateFilters';
import AdvancedFilters from '../components/AdvancedFilters';
import { Franchise } from '../../../app/services/api/generated';
import { getUserReportClient, UserReportFilterDto } from '../../../app/services/api/users/userReportClient';
import dayjs from 'dayjs';

const ALL_FRANCHISES_ID = -1;

interface UserReportFormProps {
  /** Fired after a report is successfully queued so the page can refresh status panels. */
  onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
}

const UserReportForm = (props: UserReportFormProps) => {
  const { user, selectedFranchise, selectedStore } = useContext(UserContext);
  const { t } = useTranslation('common');
  const theme = useTheme();

  // Branded palettes can leave contrastText undefined (themeProviderContext).
  const primaryContrast = theme.palette.primary?.contrastText ?? theme.palette.common.white;

  const franchises = (user?.franchises ?? []).filter(f =>
    user?.stores?.some(s => s.franchiseId === f.franchiseId),
  );

  const currentFranchise = selectedFranchise
    ?? franchises.find(f => f.franchiseId === selectedStore?.franchiseId);

  const [selectedFranchises, setSelectedFranchises] = useState<Franchise[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const isCurrentFranchiseSelected = !!currentFranchise
    && selectedFranchises.length === 1
    && selectedFranchises[0].franchiseId === currentFranchise.franchiseId;

  const isAllFranchisesSelected = selectedFranchises.length === 0;

  // Single-franchise users have nothing to toggle — the scope chips are hidden and the sole
  // franchise is selected by default.
  const showScopeChips = franchises.length > 1;

  // Default to the current franchise once known.
  useEffect(() => {
    if (currentFranchise && selectedFranchises.length === 0) {
      setSelectedFranchises([currentFranchise]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFranchise]);

  const getMaxDate = () => dayjs().format('YYYY-MM-DD');
  const getMinDate = () => new Date('1900-01-01').toISOString().split('T')[0];

  // Selecting "All Franchises" clears any explicit selection back to the whole set.
  const handleFranchiseChange = (_event: SyntheticEvent, value: Franchise[]) => {
    setSelectedFranchises(value.some((f) => f.franchiseId === ALL_FRANCHISES_ID) ? [] : value);
  };

  const handleQuickDateRange = (range: DateRange) => {
    setFromDate(range.from);
    setToDate(range.to);
  };

  const handleCurrentFranchise = () => {
    setSelectedFranchises(currentFranchise ? [currentFranchise] : []);
  };

  const handleFromDateChange = (value: string | null) => {
    setFromDate(value ?? '');
  };

  const handleToDateChange = (value: string | null) => {
    setToDate(value ?? '');
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    // Dates are required and must parse — a cleared picker would throw RangeError in toISOString.
    if (!dayjs(fromDate).isValid() || !dayjs(toDate).isValid()) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.date-range-required'));
      return;
    }

    // Empty selection means "all franchises".
    const effectiveFranchises = selectedFranchises.length === 0 ? franchises : selectedFranchises;
    if (effectiveFranchises.length === 0) return;

    setIsGenerating(true);
    setErrorMessage('');

    const filter: UserReportFilterDto = {
      startDate: new Date(fromDate + 'T00:00:00Z').toISOString(),
      endDate: new Date(toDate + 'T23:59:59.999Z').toISOString(),
      franchiseIds: effectiveFranchises.map(f => f.franchiseId as number),
    };

    getUserReportClient()
      .runUserReport(filter)
      .then(() => {
        props.onGenerated?.();
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t('reporting.user-report.error');
        props.onGenerationFailed?.(message);
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {currentFranchise && showScopeChips && (
          <Grid item
            xs={12}>
            <Typography variant="textMD"
              sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
              {t('reportingV2.scope')}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Chip
                label={t('reportingV2.current-franchise')}
                clickable
                onClick={handleCurrentFranchise}
                variant={isCurrentFranchiseSelected ? 'filled' : 'outlined'}
                aria-pressed={isCurrentFranchiseSelected}
                sx={{
                  fontWeight: isCurrentFranchiseSelected ? 600 : 400,
                  bgcolor: isCurrentFranchiseSelected ? theme.palette.primary.main : 'transparent',
                  color: isCurrentFranchiseSelected ? primaryContrast : theme.palette.text.primary,
                  borderColor: isCurrentFranchiseSelected ? theme.palette.primary.main : theme.palette.custom.gray[300],
                  '&:hover': { bgcolor: isCurrentFranchiseSelected ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
                }}
              />
              <Chip
                label={t('reporting.all-franchises')}
                clickable
                onClick={() => setSelectedFranchises([])}
                variant={isAllFranchisesSelected ? 'filled' : 'outlined'}
                aria-pressed={isAllFranchisesSelected}
                sx={{
                  fontWeight: isAllFranchisesSelected ? 600 : 400,
                  bgcolor: isAllFranchisesSelected ? theme.palette.primary.main : 'transparent',
                  color: isAllFranchisesSelected ? primaryContrast : theme.palette.text.primary,
                  borderColor: isAllFranchisesSelected ? theme.palette.primary.main : theme.palette.custom.gray[300],
                  '&:hover': { bgcolor: isAllFranchisesSelected ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
                }}
              />
            </Box>
          </Grid>
        )}
        <Grid item
          xs={12}>
          <QuickDateFilters onChange={handleQuickDateRange} />
        </Grid>
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.start-date')}
                  value={fromDate || null}
                  useCalendar={false}
                  error={showErrors && !dayjs(fromDate).isValid()}
                  onChange={handleFromDateChange}
                  shouldDisableDate={(date) => date < getMinDate() || date > (toDate !== '' ? toDate : getMaxDate())}
                  translations={{
                    clearButtonLabel: t('calendar.clearButtonLabel'),
                    todayButtonLabel: t('calendar.todayButtonLabel'),
                    cancelButtonLabel: t('calendar.cancelButtonLabel'),
                    okButtonLabel: t('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.start-date'),
                  }}
                />
              </Grid>
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.end-date')}
                  value={toDate || null}
                  useCalendar={false}
                  error={showErrors && !dayjs(toDate).isValid()}
                  onChange={handleToDateChange}
                  shouldDisableDate={(date) => date < (fromDate !== '' ? fromDate : getMinDate()) || date > getMaxDate()}
                  translations={{
                    clearButtonLabel: t('calendar.clearButtonLabel'),
                    todayButtonLabel: t('calendar.todayButtonLabel'),
                    cancelButtonLabel: t('calendar.cancelButtonLabel'),
                    okButtonLabel: t('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.end-date'),
                  }}
                />
              </Grid>
              <Grid item
                xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    limitTags={2}
                    options={[{ franchiseId: ALL_FRANCHISES_ID, franchiseName: t('reporting.all-franchises') } as Franchise, ...franchises]}
                    value={selectedFranchises}
                    onChange={handleFranchiseChange}
                    isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
                    getOptionLabel={(option) => option.franchiseName ?? ''}
                    renderOption={(props, option) => {
                      const isAll = option.franchiseId === ALL_FRANCHISES_ID;
                      const selected = isAll ? selectedFranchises.length === 0 : selectedFranchises.some((f) => f.franchiseId === option.franchiseId);
                      return (
                        <li {...props}
                          key={option.franchiseId}>
                          <Checkbox checked={selected}
                            sx={{ mr: 1 }} />
                          <ListItemText primary={option.franchiseName} />
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((franchise, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={franchise.franchiseId}
                          label={franchise.franchiseName}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('reporting.user-report.select-franchise')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {selectedFranchises.length === 0 && (
                                <Chip
                                  label={t('reporting.all-franchises')}
                                  size="small"
                                  sx={{ mr: '4px' }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {errorMessage && (
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        )}
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={isGenerating}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default UserReportForm;